import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactApexChart from 'react-apexcharts';
import { usersApi, departmentsApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { isPlatformAdmin, isSuperAdmin } from '../utils/adminAccess';
import { toneFromMap, type Tone } from '../constants/tone';

type User = {
  userId: string;
  name?: string;
  email?: string;
  role?: 'admin' | 'user' | 'viewer' | string;
  status?: string;
  createdAt?: string;
  departmentName?: string;
};

type UsersResponse = {
  users?: User[];
  count?: number;
};

type Department = {
  departmentId: string;
  name?: string;
  description?: string;
};

const USER_STATUS_TONE: Record<string, Tone> = { active: 'success', pending: 'warning', inactive: 'neutral' };

// Same donut shape as SecurityDashboard.tsx's donutOptions — copied verbatim, only the
// tooltip's unit label changed from "risks" to "users" since both donuts on this page
// break users down by a different dimension (status, role).
const donutOptions = (
  labels: string[],
  colors: string[],
  center: { value: number; label: string; color: string },
): ApexCharts.ApexOptions => ({
  chart: { type: 'donut' },
  labels,
  colors,
  stroke: { width: 0 },
  dataLabels: { enabled: false },
  legend: { position: 'bottom', fontSize: '13px' },
  plotOptions: {
    pie: {
      donut: {
        size: '68%',
        labels: {
          show: true,
          total: {
            show: true,
            showAlways: true,
            label: center.label,
            color: center.color,
            fontSize: '13px',
            fontWeight: 500,
            formatter: () => String(center.value),
          },
        },
      },
    },
  },
  tooltip: { y: { formatter: (v: number) => `${v} users` } },
});

// Fixed roles get a stable, meaningful color; any other role string the tenant happens to
// have (a legacy value, something set directly via the API) still renders instead of being
// dropped, cycling through the remaining tone-palette colors.
const ROLE_COLOR: Record<string, string> = {
  admin: 'var(--bs-primary)',
  user: 'var(--bs-info)',
  viewer: 'var(--bs-secondary)',
};
const ROLE_FALLBACK_COLORS = ['var(--bs-warning)', 'var(--bs-success)', 'var(--bs-danger)'];
const KNOWN_ROLE_ORDER = ['admin', 'user', 'viewer'];

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const displayName = user?.name || user?.email?.split('@')[0] || 'there';

  // Two different "admin" concepts, kept distinct on purpose — see utils/adminAccess.ts and
  // App.tsx's own Layout for the same split. isAdmin gates the platform-operator-only tools
  // (Field Catalog, Template Builder); isTenantAdmin gates a tenant's own admin tools (Team
  // Management, Admin Portal); isSuperAdminUser gates Super Admin. Mirrors App.tsx exactly so
  // this page never shows a quick-action link the user couldn't actually use.
  const isAdmin = isPlatformAdmin(user?.email || '');
  const isTenantAdmin = (user?.role || '').toLowerCase() === 'admin';
  const isSuperAdminUser = isSuperAdmin();
  const hasAnyQuickAction = isTenantAdmin || isAdmin || isSuperAdminUser;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: async () => {
      const [usersResponse, departments] = await Promise.all([
        usersApi.getAll(),
        departmentsApi.getAll(),
      ]);
      return {
        users: ((usersResponse as UsersResponse)?.users || []) as User[],
        departments: (departments || []) as Department[],
      };
    },
  });

  const users = data?.users || [];
  const departments = data?.departments || [];

  const activeCount = useMemo(() => users.filter((u) => u.status === 'active').length, [users]);
  const pendingCount = useMemo(() => users.filter((u) => u.status === 'pending').length, [users]);
  const inactiveCount = useMemo(() => users.filter((u) => u.status === 'inactive').length, [users]);

  const recentUsers = useMemo(
    () =>
      [...users]
        .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
        .slice(0, 5),
    [users],
  );

  const recentDepartments = departments.slice(0, 5);

  const statCards = [
    {
      label: 'Total Users',
      value: users.length,
      sub: `${departments.length} department${departments.length === 1 ? '' : 's'}`,
      icon: 'ri-team-line',
    },
    {
      label: 'Active Users',
      value: activeCount,
      sub: users.length > 0 ? `${Math.round((activeCount / users.length) * 100)}% of total` : 'No users yet',
      icon: 'ri-checkbox-circle-line',
    },
    {
      label: 'Departments',
      value: departments.length,
      sub: 'Across the organization',
      icon: 'ri-building-line',
    },
    {
      label: 'Needs Attention',
      value: pendingCount,
      sub: pendingCount > 0 ? `${pendingCount} pending invite${pendingCount === 1 ? '' : 's'}` : 'No pending invites',
      icon: 'ri-alert-line',
    },
  ];

  // Users by Status donut — fixed order, filtered to categories actually present, same
  // pattern as SecurityDashboard's donutKeys (keyed by name, not position, so filtering out
  // a middle category never misaligns index-to-color).
  const statusCounts: Record<string, number> = { Active: activeCount, Pending: pendingCount, Inactive: inactiveCount };
  const STATUS_DONUT_COLOR: Record<string, string> = {
    Active: 'var(--bs-success)',
    Pending: 'var(--bs-warning)',
    Inactive: 'var(--bs-secondary)',
  };
  const statusDonutKeys = (['Active', 'Pending', 'Inactive'] as const).filter((k) => statusCounts[k] > 0);
  const statusDonutSeries = statusDonutKeys.map((k) => statusCounts[k]);
  const statusDonutColors = statusDonutKeys.map((k) => STATUS_DONUT_COLOR[k]);
  const statusDonutCenter = { value: users.length, label: 'Total users', color: 'var(--bs-heading-color)' };

  // Users by Role donut — grouped by whatever distinct role strings actually exist in the
  // data (not assumed to be only admin/user/viewer), with known roles ordered first for a
  // stable, predictable legend and any other value still getting its own slice and color.
  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    users.forEach((u) => {
      const r = u.role || 'unspecified';
      counts[r] = (counts[r] || 0) + 1;
    });
    return counts;
  }, [users]);
  const roleKeysPresent = Object.keys(roleCounts);
  const orderedRoleKeys = [
    ...KNOWN_ROLE_ORDER.filter((r) => roleCounts[r] > 0),
    ...roleKeysPresent.filter((r) => !KNOWN_ROLE_ORDER.includes(r)),
  ];
  let roleFallbackIdx = 0;
  const roleDonutColors = orderedRoleKeys.map(
    (r) => ROLE_COLOR[r.toLowerCase()] || ROLE_FALLBACK_COLORS[roleFallbackIdx++ % ROLE_FALLBACK_COLORS.length],
  );
  const roleDonutLabels = orderedRoleKeys.map(capitalize);
  const roleDonutSeries = orderedRoleKeys.map((r) => roleCounts[r]);
  const roleDonutCenter = { value: users.length, label: 'Total users', color: 'var(--bs-heading-color)' };

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* Welcome / Hero Row */}
      <div className="np-grid np-grid-8-4">
        <div className="np-card">
          <div className="np-card-body d-flex align-items-start justify-content-between">
            <div className="flex-grow-1">
              <h2 style={{ marginBottom: 4, fontSize: '1.375rem', fontWeight: 700, color: 'var(--bs-heading-color)', lineHeight: 1.3 }}>Welcome back, {displayName}! 👋</h2>
              <p className="np-sub" style={{ marginBottom: '1rem' }}>Here's what's happening across your platform administration today.</p>
              <div className="row g-3">
                {[
                  { count: activeCount, label: 'Active', tone: 'success', icon: 'ri-checkbox-circle-line' },
                  { count: pendingCount, label: 'Pending', tone: 'warning', icon: 'ri-time-line' },
                  { count: inactiveCount, label: 'Inactive', tone: 'neutral', icon: 'ri-user-unfollow-line' },
                  { count: departments.length, label: 'Departments', tone: 'info', icon: 'ri-building-line' },
                ].map((item) => (
                  <div key={item.label} className="col-6 col-md-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className={`np-icon-badge np-tone-${item.tone}`}><i className={item.icon}></i></span>
                      <div>
                        <div className="np-tabular" style={{ fontWeight: 600, lineHeight: 1 }}>{item.count}</div>
                        <small className="np-hint">{item.label}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 d-flex gap-2 flex-wrap">
                {isTenantAdmin && (
                  <Link to="/users" className="np-btn np-btn-primary np-btn-sm"><i className="ri-user-add-line"></i>Manage Users</Link>
                )}
                <Link to="/departments" className="np-btn np-btn-ghost np-btn-sm"><i className="ri-building-line"></i>View Departments</Link>
                {isTenantAdmin && (
                  <Link to="/admin" className="np-btn np-btn-ghost np-btn-sm"><i className="ri-settings-3-line"></i>Admin Portal</Link>
                )}
              </div>
            </div>
            <i className="ri-admin-line ms-3 d-none d-md-block" style={{ fontSize: '3rem', color: 'var(--bs-primary)', opacity: 0.25 }}></i>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Quick Actions</h2></div>
          <div className="np-card-body d-flex flex-column gap-2">
            {isTenantAdmin && (
              <Link to="/users" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-team-line"></i>Team Management</Link>
            )}
            {isTenantAdmin && (
              <Link to="/admin" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-settings-3-line"></i>Admin Portal</Link>
            )}
            {isAdmin && (
              <Link to="/admin/field-catalog" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-database-2-line"></i>Field Catalog</Link>
            )}
            {isAdmin && (
              <Link to="/admin/templates" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-layout-grid-line"></i>Template Builder</Link>
            )}
            {isSuperAdminUser && (
              <Link to="/super-admin" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-shield-keyhole-line"></i>Super Admin</Link>
            )}
            {!hasAnyQuickAction && (
              <p className="np-sub mb-0">No admin actions available for your account.</p>
            )}
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="np-grid np-grid-4">
        {statCards.map((card) => (
          <div key={card.label} className="np-card">
            <div className="np-card-body np-stat">
              <span className="np-stat-icon np-tone-primary"><i className={card.icon}></i></span>
              <p className="np-stat-label">{card.label}</p>
              <p className="np-stat-value np-tabular">{card.value}</p>
              <p className="np-stat-trend" style={{ color: card.label === 'Needs Attention' && pendingCount > 0 ? 'var(--bs-danger)' : 'var(--bs-success)' }}>
                {card.label === 'Needs Attention' && pendingCount > 0
                  ? <><i className="ri-alert-line"></i>{card.sub}</>
                  : <><i className="ri-arrow-up-line"></i>{card.sub}</>}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="np-grid np-grid-2">
        <div className="np-card">
          <div className="np-card-head"><h2>Users by Status</h2></div>
          <div className="np-card-body">
            {statusDonutKeys.length === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-pie-chart-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>No user data yet
              </div>
            ) : (
              <ReactApexChart type="donut" height={300} series={statusDonutSeries}
                options={donutOptions(statusDonutKeys, statusDonutColors, statusDonutCenter)} />
            )}
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Users by Role</h2></div>
          <div className="np-card-body">
            {orderedRoleKeys.length === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-pie-chart-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>No role data yet
              </div>
            ) : (
              <ReactApexChart type="donut" height={300} series={roleDonutSeries}
                options={donutOptions(roleDonutLabels, roleDonutColors, roleDonutCenter)} />
            )}
          </div>
        </div>
      </div>

      {/* Recently added users + departments */}
      <div className="np-grid np-grid-2">
        <div className="np-card">
          <div className="np-card-head">
            <h2>Recently Added Users</h2>
            <Link to="/users" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Name</th><th>Role</th><th>Status</th></tr></thead>
                <tbody>
                  {recentUsers.length === 0 ? (
                    <tr><td colSpan={3} className="text-center np-sub py-4"><i className="ri-team-line me-1"></i>No users found</td></tr>
                  ) : recentUsers.map((u) => (
                    <tr key={u.userId}>
                      <td className="np-row-title-static">{u.name || u.email || u.userId}</td>
                      <td>{u.role ? <span className="np-chip np-tone-neutral">{u.role}</span> : <span className="np-sub">—</span>}</td>
                      <td>{u.status ? <span className={`np-chip np-tone-${toneFromMap(USER_STATUS_TONE, u.status, 'neutral')}`}>{u.status}</span> : <span className="np-sub">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head">
            <h2>Departments</h2>
            <Link to="/departments" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Name</th><th>Description</th></tr></thead>
                <tbody>
                  {recentDepartments.length === 0 ? (
                    <tr><td colSpan={2} className="text-center np-sub py-4"><i className="ri-building-line me-1"></i>No departments found</td></tr>
                  ) : recentDepartments.map((d) => (
                    <tr key={d.departmentId}>
                      <td><Link to="/departments" className="np-row-title">{d.name || d.departmentId}</Link></td>
                      <td className="np-sub">{d.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
